import { ethers } from 'ethers';
import { Web3Storage } from 'web3.storage';

// Contract ABIs - importing from src/artifacts
const ContentAuthenticationArtifact = require('../artifacts/contracts/ContentAuthentication.json');
const ReputationManagerArtifact = require('../artifacts/contracts/ReputationManager.json');
const TrustRecordArtifact = require('../artifacts/contracts/TrustRecord.json');

// Contract ABIs
const ContentAuthenticationABI = ContentAuthenticationArtifact.abi;
const ReputationManagerABI = ReputationManagerArtifact.abi;
const TrustRecordABI = TrustRecordArtifact.abi;

// Contract addresses (you'll need to replace these with your deployed contract addresses)
const ContentAuthenticationAddress = '0xBA40dc0dFBC122FFCa1817a32B6228a2888932cD';
const ReputationManagerAddress = '0x057e628A57DaC2B5D86A82A16dC33BFa9C518357';
const TrustRecordAddress = '0x6a4a5d3c88F169A7f6Ca5cB520725A25E99A5307';

// Initialize Web3.Storage client
const web3StorageClient = new Web3Storage({ token: process.env.REACT_APP_WEB3_STORAGE_TOKEN });

export const connectWallet = async () => {
  if (typeof window.ethereum === 'undefined' || !window.ethereum.isMetaMask) {
    throw new Error('Please install MetaMask!');
  }

  // Request account access
  const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });

  if (!accounts || accounts.length === 0) {
    throw new Error('No accounts found. Please make sure you are logged into MetaMask.');
  }

  // Create provider and signer
  const provider = new ethers.BrowserProvider(window.ethereum);
  const signer = await provider.getSigner();
  const address = await signer.getAddress();

  return {
    provider,
    signer,
    address,
  };
};

export const getContracts = async signer => {
  const contentAuth = new ethers.Contract(
    ContentAuthenticationAddress,
    ContentAuthenticationABI,
    signer
  );

  const reputationManager = new ethers.Contract(
    ReputationManagerAddress,
    ReputationManagerABI,
    signer
  );

  const trustRecord = new ethers.Contract(TrustRecordAddress, TrustRecordABI, signer);

  return {
    contentAuth,
    reputationManager,
    trustRecord,
  };
};

export const uploadToIPFS = async file => {
  if (!process.env.REACT_APP_WEB3_STORAGE_TOKEN) {
    throw new Error('Web3.Storage token not configured');
  }

  try {
    // Create a File object array
    const files = [new File([file], file.name, { type: file.type })];

    // Upload to IPFS through Web3.Storage
    const cid = await web3StorageClient.put(files, {
      wrapWithDirectory: false,
    });

    // Return the IPFS hash/CID
    return `ipfs://${cid}`;
  } catch (error) {
    throw new Error(`Failed to upload to IPFS: ${error.message}`);
  }
};

export const registerContent = async (contentAuth, contentHash, contentType, isAIGenerated) => {
  try {
    const tx = await contentAuth.registerContent(contentHash, contentType, isAIGenerated);
    await tx.wait();
    return tx.hash;
  } catch (error) {
    throw new Error(`Failed to register content: ${error.message}`);
  }
};

export const validateContent = async (contentAuth, contentHash) => {
  try {
    const tx = await contentAuth.validateContent(contentHash);
    await tx.wait();
    return tx.hash;
  } catch (error) {
    throw new Error(`Failed to validate content: ${error.message}`);
  }
};

export const stakeForValidation = async (contentAuth, amount) => {
  try {
    const tx = await contentAuth.stakeForValidation({ value: amount });
    await tx.wait();
    return tx.hash;
  } catch (error) {
    throw new Error(`Failed to stake: ${error.message}`);
  }
};

export const getContentDetails = async (contentAuth, contentHash) => {
  try {
    return await contentAuth.getContentDetails(contentHash);
  } catch (error) {
    throw new Error(`Failed to get content details: ${error.message}`);
  }
};

export const getReputation = async (reputationManager, address) => {
  try {
    return await reputationManager.getTotalReputation(address);
  } catch (error) {
    throw new Error(`Failed to get reputation: ${error.message}`);
  }
};
